import { createContext, useContext, useState, useEffect, useCallback, useRef, startTransition } from 'react';
import { useNavigate } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import { toast } from 'react-toastify';
import { isTokenExpired } from '../utils/tokenUtils';

// Create the context
const AuthContext = createContext();

export const useAuth = () => useContext(AuthContext);

// Token refresh buffer in milliseconds (5 minutes before expiry)
// This allows proactive token refresh before the token actually expires
const TOKEN_REFRESH_BUFFER_MS = 5 * 60 * 1000;
const TOKEN_REFRESH_TIMEOUT_MS = 10000;

const DEBUG_AUTH = false;
const authLog = (...args) => DEBUG_AUTH && console.log(`[Auth ${new Date().toISOString()}]`, ...args);

/**
 * Helper function to set Sentry user context
 * Ensures consistent user identification across error tracking, session replay, and user feedback
 * This function is intentionally defensive: any failure in Sentry integration must not impact authentication flow.
 * @param {Object} UserData - The user data object containing user details
 */
const SetSentryUserContext = (UserData) => {
    try {
        // When no user data is provided, clear the Sentry user context.
        if (!UserData) {
            // Guard against Sentry not being initialized correctly.
            if (typeof Sentry?.setUser === 'function') {
                Sentry.setUser(null);
            }
            return;
        }

        // Construct full name from firstName and lastName if fullName is not available
        let FullName = UserData.fullName;
        if (!FullName) {
            const FirstName = UserData.firstName || '';
            const LastName = UserData.lastName || '';
            const ConstructedName = `${FirstName} ${LastName}`.trim();
            // Use undefined if name is empty so that Sentry does not receive an empty string.
            FullName = ConstructedName || undefined;
        }

        // Extract email - backend returns UserEmail (capital E)
        const Email = UserData.userEmail || UserData.UserEmail || UserData.email;
        
        // Extract username - use email as username for Sentry display
        const Username = Email;

        // Only attempt to set Sentry user context if the Sentry SDK is available.
        if (typeof Sentry?.setUser === 'function') {
            const SentryUserContext = {
                id: UserData.userID?.toString() || UserData.UserID?.toString() || UserData.id?.toString(),
                email: Email,
                username: Username,
                name: FullName,
            };
            
            Sentry.setUser(SentryUserContext);
        }
    } catch (Error) {
        Sentry.captureException(Error);
        // Log locally to avoid breaking authentication flows if Sentry integration misbehaves.
        // Do not rethrow or call back into Sentry here, as Sentry may be the source of the error.
        console.error('Failed to set Sentry user context:', Error);
    }
};

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [permissions, setPermissions] = useState([]);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();
    
    // Track ongoing token refresh to prevent concurrent refreshes
    const tokenRefreshPromiseRef = useRef(null);

    // Load user data from localStorage on initial render
    useEffect(() => {
        (async () => {
            const storedUser = localStorage.getItem('user');
            const storedPermissions = localStorage.getItem('permissions');

            if (storedUser) {
                const ParsedUser = JSON.parse(storedUser);

                // Reject expired tokens on initial load to prevent rendering
                // protected pages with stale credentials
                if (isTokenExpired(ParsedUser?.token)) {
                    localStorage.removeItem('user');
                    localStorage.removeItem('permissions');
                    setLoading(false);
                    return;
                }

                setUser(ParsedUser);
                setPermissions(storedPermissions ? JSON.parse(storedPermissions) : []);

                // Set Sentry user context for error tracking and session replay
                SetSentryUserContext(ParsedUser);
            }

            setLoading(false);
        })();
    }, []);

    const refreshAccessToken = useCallback(async () => {
        if (tokenRefreshPromiseRef.current) {
            // ⚠️  A caller is already waiting — this is the queue-behind-the-mutex scenario
            authLog('⏳ QUEUED — another refresh is in progress, awaiting existing promise');
            const t = performance.now();
            const result = await tokenRefreshPromiseRef.current;
            authLog(`🔓 UNBLOCKED after ${(performance.now() - t).toFixed(0)}ms, result=${result}`);
            return result;
        }

        authLog('🔄 REFRESH STARTED');
        const startTime = performance.now();

        tokenRefreshPromiseRef.current = (async () => {
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), TOKEN_REFRESH_TIMEOUT_MS);
            try {
                const storedUser = JSON.parse(localStorage.getItem('user'));
                if (!storedUser || !storedUser.refreshToken) {
                    authLog('⚠️ No refresh token found — aborting refresh');
                    return false;
                }

                const response = await fetch('/api/User/refresh-token', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ refreshToken: storedUser.refreshToken }),
                    signal: controller.signal,
                });

                if (!response.ok) {
                    authLog(`❌ Refresh HTTP ${response.status} after ${(performance.now() - startTime).toFixed(0)}ms`);
                    return false;
                }

                const result = await response.json();
                const accessToken = result.AccessToken || result.accessToken;
                const refreshToken = result.RefreshToken || result.refreshToken;

                if (accessToken && refreshToken) {
                    const updatedUser = { ...storedUser, token: accessToken, refreshToken };
                    localStorage.setItem('user', JSON.stringify(updatedUser));
                    startTransition(() => setUser(updatedUser));
                    authLog(`✅ REFRESH COMPLETE in ${(performance.now() - startTime).toFixed(0)}ms`);
                    return true;
                }

                authLog(`⚠️ Refresh response missing tokens after ${(performance.now() - startTime).toFixed(0)}ms`);
                return false;
            } catch (e) {
                Sentry.captureException(e);
                if (e.name === 'AbortError') {
                    authLog(`⏰ REFRESH TIMED OUT after ${(performance.now() - startTime).toFixed(0)}ms — proceeding with existing token`);
                    return true;
                }
                authLog(`❌ Refresh threw: ${e.message}`);
                console.error('Error refreshing token:', e);
                return false;
            } finally {
                clearTimeout(timeoutId);
                tokenRefreshPromiseRef.current = null;
            }
        })();

        return await tokenRefreshPromiseRef.current;
    }, []);

    // Login function that calls API and stores the result
    const login = async (email, password, rememberMe = false) => {
        setUser(null);
        setPermissions([]);
        localStorage.removeItem('user');
        localStorage.removeItem('permissions');

        try {
            const response = await fetch('/api/User/login', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ email, password, rememberMe }),
            });
            
            const result = await response.json();
            if (result.success) {
                const UserData = {
                    ...result.data.userDetails,
                    token: result.data.accessToken,
                    refreshToken: result.data.refreshToken,
                };

                // Debug logging to verify token extraction (removed to prevent cleartext token logging)

                setUser(UserData);
                localStorage.setItem('user', JSON.stringify(UserData));
                setPermissions(result.data.permissions || []);
                localStorage.setItem('permissions', JSON.stringify(result.data.permissions || []));
                
                // Set Sentry user context for error tracking and session replay
                SetSentryUserContext(UserData);
                
                return true;
            } else {
                setUser(null);
                setPermissions([]);
                localStorage.removeItem('user');
                localStorage.removeItem('permissions');
                return false;
            }
        } catch (catchError) {
            Sentry.captureException(catchError);
            setUser(null);
            setPermissions([]);
            localStorage.removeItem('user');
            localStorage.removeItem('permissions');
            return false;
        }
    };

    // Logout function that clears the stored data
    // ShowToast parameter allows showing a toast notification when session expires
    const logout = useCallback((ShowToast = false) => {
        setUser(null);
        setPermissions([]);
        localStorage.removeItem('user');
        localStorage.removeItem('permissions');
        
        // Clear Sentry user context on logout
        SetSentryUserContext(null);
        
        // Show toast notification if requested (e.g., for session expiry)
        // Position is set to top-center (vs. global default top-right) to make
        // session expiry notifications more prominent and centered for user attention
        if (ShowToast) {
            toast.error('Your session has expired. Please log in again.', {
                position: "top-center",
            });
        }
        
        navigate('/');
    }, [navigate]);

    // Check if user has permission for a specific page route
    const hasPermission = (pageRoute) => {
        if (!permissions || permissions.length === 0) return false;
        return permissions.some(p => p.pageRoute === pageRoute);
    };

    // Check if user has enquiry-only (read-only) access to a page
    const isEnquiryOnly = (pageRoute) => {
        if (!permissions || permissions.length === 0) return false;
        const permission = permissions.find(p => p.pageRoute === pageRoute);
        return permission ? permission.enquiryOnly : false;
    };

    const apiFetch = useCallback(async (url, options = {}, timeout = 1500000) => {
        const storedUser = JSON.parse(localStorage.getItem('user'));
        
        if (storedUser && storedUser.token) {
            let shouldRefreshToken;
            let tokenExpiry;
            try {
                // Parse token expiry and add a buffer to proactively refresh before expiration
                tokenExpiry = JSON.parse(atob(storedUser.token.split('.')[1])).exp * 1000;
                shouldRefreshToken = Date.now() >= (tokenExpiry - TOKEN_REFRESH_BUFFER_MS);
            } catch (error) {
                Sentry.captureException(error);
                /**
                 * If token parsing fails, it indicates that the stored token is malformed or corrupted.
                 * In this situation we must not continue using the token, as that would cause repeated
                 * authentication failures and bypass our proactive refresh logic.
                 *
                 * We log a detailed warning (including the error message) and immediately log
                 * the user out so that a clean authentication flow can occur. We then throw an error so
                 * callers of apiFetch are aware that the session is no longer valid.
                 *
                 * NOTE: We intentionally do not log the token contents to avoid leaking sensitive data.
                 */
                console.warn('[apiFetch] Malformed or unparseable token detected. Logging out user to prevent use of invalid token.', {
                    errorMessage: error?.message || 'Unknown token parsing error'
                });
                
                // Ensure any invalid authentication state is cleared before proceeding
                logout(true); // Pass true to show session expired toast
                
                // Surface a clear, user-friendly error message to the caller
                throw new Error('Authentication token is invalid or corrupted. Please log in again.', { cause: error });
            }
            
            if (shouldRefreshToken) {
                authLog(`🔒 apiFetch("${url}") — token expiring, calling refreshAccessToken`);
                const refreshed = await refreshAccessToken();
                if (!refreshed) {
                    // If token refresh failed, logout immediately and show toast notification
                    // This prevents sending expired tokens to the server
                    logout(true); // Pass true to show session expired toast
                    throw new Error('Session expired. Please log in again');
                }
                // Use the refreshed token
                const currentUser = JSON.parse(localStorage.getItem('user'));
                options.headers = {
                    ...options.headers,
                    Authorization: `Bearer ${currentUser?.token ?? storedUser.token}`,
                };
            } else {
                options.headers = {
                    ...options.headers,
                    Authorization: `Bearer ${storedUser.token}`,
                };
            }
        }
        const controller = new AbortController();
        const id = setTimeout(() => controller.abort(), timeout);
        try {
            const response = await fetch(url, { ...options, signal: controller.signal });
            clearTimeout(id);
            
            // Check for token expiration header from backend
            // (reserved for future use, e.g., handling soft-expired tokens)
            
            // Handle 401 Unauthorized - authentication failure
            // Exception: Do NOT logout for third-party API endpoints that may return 401
            // due to missing API keys or third-party service issues (not user session issues)
            if (response.status === 401) {
                // List of third-party API path prefixes where 401 should not trigger logout
                const ThirdPartyApiPrefixes = [
                    '/api/RetailDynamics/', // FootFall Dashboard third-party API
                ];
                
                // Extract the pathname from URL (handles both relative and absolute URLs)
                let Pathname;
                try {
                    Pathname = new URL(url, window.location.origin).pathname;
                } catch (catchError) {
                    Sentry.captureException(catchError);
                    // If URL parsing fails, treat url as pathname directly
                    Pathname = url;
                }
                
                // Check if this is a third-party API endpoint by checking if pathname starts with any prefix
                const IsThirdPartyApi = ThirdPartyApiPrefixes.some(prefix => Pathname.startsWith(prefix));
                
                if (IsThirdPartyApi) {
                    // For third-party APIs, return the response and let the calling code handle it
                    // Do NOT log out the user for third-party API authentication issues
                    console.warn(`Third-party API returned 401 for ${url}. This may indicate missing API credentials on the backend.`);
                    return response;
                } else {
                    // For our own API endpoints, 401 means the user's session has expired
                    logout(true); // Pass true to show session expired toast
                    throw new Error('Session expired. Please log in again');
                }
            }
            
            // Handle 403 Forbidden - only logout if it's an account-level issue
            // Do NOT logout for resource-level authorization (property access, external API limits, etc.)
            if (response.status === 403) {
                // Check if the response indicates account-level issue vs resource-level authorization
                let shouldLogout = false;
                try {
                    // Clone the response so we can read it without consuming the original
                    const clonedResponse = response.clone();
                    const contentType = response.headers.get('content-type');
                    let errorMessage = '';
                    
                    // Try to parse response body (JSON or plain text)
                    if (contentType && contentType.includes('application/json')) {
                        const errorData = await clonedResponse.json();
                        errorMessage = errorData?.error || errorData?.message || '';
                    } else {
                        // Plain text response (like from UserActiveCheckMiddleware)
                        errorMessage = await clonedResponse.text();
                    }
                    
                    // Only logout if the error explicitly indicates account disabled or similar
                    const accountDisabledKeywords = ['account disabled', 'account is inactive', 'user disabled', 'user is inactive', 'user account is inactive'];
                    
                    if (accountDisabledKeywords.some(keyword => errorMessage.toLowerCase().includes(keyword))) {
                        shouldLogout = true;
                    }
                } catch (parseError) {
                    Sentry.captureException(parseError);
                    // If we can't parse the response, don't logout - let the calling code handle it
                    console.warn('Could not parse 403 response:', parseError);
                }
                
                // Perform logout and throw error outside the try-catch to avoid swallowing logout errors
                if (shouldLogout) {
                    logout(true); // Pass true to show notification when account is disabled
                    throw new Error('Account disabled or inactive. Please contact support.');
                }
                // For other 403 errors (property access, external API limits, etc.), don't logout
                // Let the calling code handle the 403 appropriately
            }
            
            return response;
        } catch (err) {
            Sentry.captureException(err);
            clearTimeout(id);
            throw err;
        }
    }, [refreshAccessToken, logout]);

    // Refresh permissions from the backend and update state/localStorage
    const refreshPermissions = useCallback(async () => {
        // Don't attempt to refresh if user is not logged in
        if (!user || !user.token) {
            return false;
        }
        
        try {
            const response = await apiFetch('/api/User/permissions');
            const result = await response.json();
            if (result.success) {
                const newPermissions = result.data || [];
                localStorage.setItem('permissions', JSON.stringify(newPermissions));
                startTransition(() => setPermissions(newPermissions));
                return true;
            } else {
                // If the account is inactive, log out immediately and show toast
                if (result.message && result.message.toLowerCase().includes('inactive')) {
                    logout(true); // Pass true to show notification
                }
                // Server returned failure but not 401 - could be temporary issue, don't logout
                return false;
            }
        } catch (e) {
            Sentry.captureException(e);
            // If session expired error was thrown by apiFetch, user is already logged out
            if (e.message === 'Session expired. Please log in again') {
                return false;
            }
            // For network errors or other issues, don't log to avoid console spam
            return false;
        }
    }, [apiFetch, logout, user]);

    // Refresh user details from the backend and update state/localStorage
    // Call this after updating user settings to ensure the context has current data
    const refreshUser = useCallback(async () => {
        // Don't attempt to refresh if user is not logged in
        if (!user || !user.token) {
            return false;
        }
        
        try {
            const response = await apiFetch('/api/User/me');
            const result = await response.json();
            if (result.success && result.data) {
                // Preserve tokens from the current user object while updating other fields
                const StoredUser = JSON.parse(localStorage.getItem('user'));
                const UpdatedUser = {
                    ...StoredUser,
                    ...result.data,
                    token: StoredUser?.token || user.token,
                    refreshToken: StoredUser?.refreshToken || user.refreshToken,
                };
                setUser(UpdatedUser);
                localStorage.setItem('user', JSON.stringify(UpdatedUser));
                
                // Update Sentry user context with refreshed user data
                SetSentryUserContext(UpdatedUser);
                
                return true;
            }
            return false;
        } catch (e) {
            Sentry.captureException(e);
            console.error('Error refreshing user:', e);
            return false;
        }
    }, [apiFetch, user]);

    // Proactively log out when the JWT access token expires.
    // Checks on a 60-second interval AND whenever the tab becomes visible
    // (e.g. user switches back from another tab or wakes their laptop).
    useEffect(() => {
        if (!user?.token) return;

        const checkExpiry = () => {
            if (isTokenExpired(user.token)) {
                logout(true);
            }
        };

        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
                checkExpiry();
            }
        };

        const intervalId = setInterval(checkExpiry, 60_000);
        document.addEventListener('visibilitychange', handleVisibilityChange);

        return () => {
            clearInterval(intervalId);
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, [user, logout]);

    // Background job: poll permissions at intervals, retry on failure
    useEffect(() => {
        let intervalId;
        let stopped = false;
        const POLL_INTERVAL = 30000; // Changed from 5s to 30s to reduce server load
        let consecutiveErrors = 0;
        const MAX_CONSECUTIVE_ERRORS = 3;

        const pollPermissions = async () => {
            // Only poll if user is logged in
            if (!user || stopped) return;

            if (tokenRefreshPromiseRef.current) {
                authLog('⏭️ pollPermissions SKIPPED — token refresh in progress');
                return;
            }
            authLog('🔁 pollPermissions FIRING');

            // Check if user is still logged in before each attempt
            const currentUser = JSON.parse(localStorage.getItem('user'));
            if (!currentUser || !currentUser.token) {
                // User logged out during polling, stop
                return;
            }
            
            try {
                const success = await refreshPermissions();
                if (success) {
                    consecutiveErrors = 0; // Reset error counter on success
                }
            } catch (e) {
                Sentry.captureException(e);
                consecutiveErrors++;
                console.error(`Permission refresh error (${consecutiveErrors}/${MAX_CONSECUTIVE_ERRORS}):`, e.message);
                
                // If we've had too many consecutive errors, stop polling to avoid hammering the server
                if (consecutiveErrors >= MAX_CONSECUTIVE_ERRORS) {
                    console.warn('Too many consecutive permission refresh errors. Stopping background polling.');
                    stopped = true;
                    if (intervalId) clearInterval(intervalId);
                }
            }
        };

        if (user && user.token) {
            // Don't start polling immediately - wait for first interval
            // This gives the backend time to fully process the login
            intervalId = setInterval(() => {
                pollPermissions();
            }, POLL_INTERVAL);
        }
        
        return () => {
            stopped = true;
            if (intervalId) clearInterval(intervalId);
        };
    }, [user, refreshPermissions]);

    // Provide the context values to children components
    return (
        <AuthContext.Provider value={{
            user,
            permissions,
            login,
            logout,
            hasPermission,
            isEnquiryOnly,
            loading,
            apiFetch,
            refreshPermissions,
            refreshUser,
        }}>
            {children}
        </AuthContext.Provider>
    );
};